import { Edit, Person } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserRolesEdit } from './user-roles-edit'
import { NewUser } from './new-user'
import { useCreateExternalUserMutation, useUsersQuery } from 'services/users'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { EUserRoles, IUser } from 'types/users'
import { toast } from 'react-toastify'

export const roles = [
  EUserRoles.COMMISSION_MEMBER,
  EUserRoles.COMMITTEE_MEMBER,
  EUserRoles.PROCUREMENT_SPECIALIST,
  EUserRoles.ISP_SPECIALIST,
]

interface Props {
  organizationId?: string
}

export const Users: FunctionComponent<Props> = ({ organizationId }) => {
  const { t } = useTranslation()
  const [currentlyEditing, setCurrentlyEditing] = useState<any>()
  const [addUserOpen, setAddUserOpen] = useState(false)
  const [searchParams, setSearchParams] = useState<{
    name: string
    email: string
    pin: string
    roles: string[]
  }>({ name: '', email: '', pin: '', roles: [] })
  const [firstName, setFirstName] = useState<string>('')
  const [checkedList, setCheckedList] = useState<string[]>([])
  const [allSelected, setAllSelected] = useState(false)

  const [
    createUser,
    { isLoading: isCreateUserLoading, isError: isCreatUserError, isSuccess },
  ] = useCreateExternalUserMutation()

  const { data, isLoading, isError } = useUsersQuery(searchParams, {
    refetchOnMountOrArgChange: true,
  })

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
  }, [isSuccess, t])

  if (isLoading) return <Loading fullPage={false} />
  if (isError) return <Error />
  if (data?.items === undefined) {
    return <></>
  }

  const handleAdd = () => {
    setAddUserOpen((prevState) => !prevState)
  }

  const handleEdit = () => {}

  const handleDeactivate = (user: IUser) => {
    createUser({ ...user, active: false })
  }

  const handleSearchUser = () => {
    setSearchParams({ ...searchParams, name: firstName })
  }

  const handleChangeCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    if (checked) {
      setSearchParams({ ...searchParams, roles: [...checkedList, value] })
    } else {
      setSearchParams({
        ...searchParams,
        roles: checkedList.filter((item) => item != value),
      })
    }
  }

  const handleAllSelect = (e: any) => {
    const { checked } = e.target
    if (checked) {
      setSearchParams({ ...searchParams, roles })
    } else {
      setSearchParams({ ...searchParams, roles: [] })
    }
    setAllSelected(checked)
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight="bold" variant="h6">
          {t('users')}
        </Typography>
        <Button
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          onClick={handleAdd}
        >
          {t('add-new-user')}
        </Button>
      </Box>

      <Grid container mt={2}>
        <Grid item xs={4}>
          <Typography fontWeight="bold" variant="subtitle1">
            {t('filters-by-roles')}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={allSelected} onClick={handleAllSelect} />}
            label={t('select-all')}
          />

          {roles.map((role, index) => (
            <Box key={index}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={role}
                    checked={searchParams?.roles.includes(role)}
                    onChange={handleChangeCheckbox}
                  />
                }
                label={t(role.toLowerCase())}
              />
            </Box>
          ))}
        </Grid>
        <Grid item xs={8}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            gap={2}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={t('search-based-on-name')}
              onChange={(e) => setFirstName(e.target.value as string)}
            />
            <Button
              onClick={handleSearchUser}
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            >
              {t('search')}
            </Button>
          </Box>
          {data.items.map((user) => (
            <Box
              key={user.id}
              border={1}
              borderColor="divider"
              padding={2}
              borderRadius={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography fontWeight="bold" variant="subtitle1">
                {user?.firstName + ' ' + user?.lastName}
              </Typography>
              <Typography>
                {user?.userRoles.map((role) => t(role.toLowerCase())).join(', ')}
              </Typography>
              <Box>
                <IconButton
                  onClick={() => {
                    setCurrentlyEditing(user)
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton onClick={() => handleDeactivate(user)}>
                  <Person color="error" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Grid>
      </Grid>

      {currentlyEditing && (
        <UserRolesEdit
          user={currentlyEditing}
          handleClose={() => setCurrentlyEditing(null)}
        />
      )}

      {addUserOpen && (
        <NewUser
          handleClose={handleAdd}
          roles={roles}
          organizationId={organizationId}
        />
      )}
    </>
  )
}
